import { CloudDownloadOutlined, EnvironmentOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type ChinaRegionNode, type RegionRow, type RegionSaveRequest } from '@hq/api-sdk';
import { flattenChinaRegions, type FlatRegionItem } from '@hq/utils';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { hq } from '../session';

/** 区划层级：1 省 / 2 市 / 3 区县 / 4 街道（后端 `sys_region.region_level`，校验 1–4）。 */
const LEVEL_META: Record<number, { label: string; color: string }> = {
  1: { label: '省', color: 'purple' },
  2: { label: '市', color: 'geekblue' },
  3: { label: '区县', color: 'cyan' },
  4: { label: '街道', color: 'green' }
};

const LEVEL_OPTIONS = Object.entries(LEVEL_META).map(([value, meta]) => ({
  value: Number(value),
  label: `${meta.label}（${value}）`
}));

const LEVEL_FILTER = [
  { label: '全部层级', value: 0 },
  { label: '省', value: 1 },
  { label: '市', value: 2 },
  { label: '区县', value: 3 },
  { label: '街道', value: 4 }
];

/** 全国区划数据源：随前端产物发布的静态 JSON（省/市/区三级，约 3200 条）。 */
const CHINA_REGIONS_URL = `${import.meta.env.BASE_URL}china-regions.json`;

interface RegionFormValues {
  regionCode: string;
  regionName: string;
  regionLevel: number;
  parentCode?: string;
  lng?: number | null;
  lat?: number | null;
}

type RegionNode = RegionRow & { children?: RegionNode[] };

/** 扁平列表按 parentCode 组装成树；父级被筛掉的行降级为根节点，保证不丢数据。 */
function buildTree(rows: RegionRow[]): RegionNode[] {
  const map = new Map<string, RegionNode>();
  for (const row of rows) map.set(row.regionCode, { ...row });

  const roots: RegionNode[] = [];
  for (const row of rows) {
    const node = map.get(row.regionCode)!;
    const parent = row.parentCode && row.parentCode !== '0' ? map.get(row.parentCode) : undefined;
    if (parent) {
      parent.children = [...(parent.children ?? []), node];
    } else {
      roots.push(node);
    }
  }
  return roots;
}

/**
 * 行政区划字典（`sys_region`）：数据源改为全国行政区划（`public/china-regions.json`），
 * 支持一键导入省/市/区三级；机构、医共体、运营仓的地址登记都以本字典为级联选项。
 * 列表门禁 `sys:tenant:view`、导入/保存门禁 `sys:region:manage`，且要求 `portal=SYSTEM`。
 */
export default function Regions() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [level, setLevel] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<RegionRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [purgeManual, setPurgeManual] = useState(false);
  const [source, setSource] = useState<FlatRegionItem[] | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [form] = Form.useForm<RegionFormValues>();

  const query = useQuery({ queryKey: ['regions'], queryFn: () => hq.tenant.regions() });
  const all = useMemo(() => query.data ?? [], [query.data]);

  // 国标数据源按需加载（543KB），只在进入本页时拉一次
  const loadSource = useCallback(async () => {
    try {
      const response = await fetch(CHINA_REGIONS_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const tree = (await response.json()) as ChinaRegionNode[];
      setSource(flattenChinaRegions(tree));
      setSourceError(null);
    } catch (err) {
      setSourceError(`本地行政区划数据加载失败：${err instanceof Error ? err.message : String(err)}`);
    }
  }, []);

  useEffect(() => {
    void loadSource();
  }, [loadSource]);

  const sourceStats = useMemo(() => {
    const rows = source ?? [];
    return {
      province: rows.filter((row) => row.level === 1).length,
      city: rows.filter((row) => row.level === 2).length,
      district: rows.filter((row) => row.level === 3).length,
      total: rows.length
    };
  }, [source]);

  const dbStats = useMemo(
    () => ({
      province: all.filter((row) => row.regionLevel === 1).length,
      city: all.filter((row) => row.regionLevel === 2).length,
      district: all.filter((row) => row.regionLevel === 3).length
    }),
    [all]
  );

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return all.filter((row) => {
      if (level && row.regionLevel !== level) return false;
      if (!kw) return true;
      return row.regionName.toLowerCase().includes(kw) || row.regionCode.includes(kw);
    });
  }, [all, level, keyword]);

  const treeData = useMemo(() => buildTree(filtered), [filtered]);

  /** 上级候选：排除自身（后端按 region_code 关联，不允许形成自环）。 */
  const parentOptions = useMemo(
    () =>
      all
        .filter((row) => row.regionCode !== editing?.regionCode)
        .map((row) => ({
          value: row.regionCode,
          label: `${row.regionName}（${row.regionCode}）· L${row.regionLevel}`
        })),
    [all, editing?.regionCode]
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ regionLevel: 3 });
    setOpen(true);
  };

  const openEdit = (row: RegionRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      regionCode: row.regionCode,
      regionName: row.regionName,
      regionLevel: row.regionLevel,
      parentCode: row.parentCode && row.parentCode !== '0' ? row.parentCode : undefined,
      lng: row.lng,
      lat: row.lat
    });
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const body: RegionSaveRequest = {
      ...(editing ? { id: editing.id } : {}),
      regionCode: values.regionCode.trim(),
      regionName: values.regionName.trim(),
      regionLevel: values.regionLevel,
      parentCode: values.regionLevel === 1 ? '0' : values.parentCode?.trim() || '0',
      lng: values.lng ?? null,
      lat: values.lat ?? null
    };

    setSaving(true);
    try {
      await hq.tenant.saveRegion(body);
      message.success(editing ? '区划已更新' : '区划已新增');
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['regions'] });
      await queryClient.invalidateQueries({ queryKey: ['sys', 'region', 'options'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: RegionRow) => {
    try {
      await hq.tenant.removeRegion(row.id);
      message.success('区划已删除');
      await queryClient.invalidateQueries({ queryKey: ['regions'] });
      await queryClient.invalidateQueries({ queryKey: ['sys', 'region', 'options'] });
    } catch (err) {
      // 有下级区划或被租户引用时后端返回 42200，原样提示给运营人员
      message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
    }
  };

  const runImport = (rows: FlatRegionItem[]) => {
    modal.confirm({
      title: '导入全国行政区划',
      content: (
        <Space direction="vertical" size={4}>
          <span>
            将写入 {sourceStats.province} 省 / {sourceStats.city} 市 / {sourceStats.district} 区县，共 {sourceStats.total} 条。
          </span>
          <span>编码相同则更新名称与层级，不删除既有区划（勾选「清理历史手工区划」才会清理）。</span>
          {purgeManual ? (
            <Typography.Text type="warning">
              已勾选清理：导入后不在国标清单内、且未被租户/机构引用的手工区划会被逻辑删除。
            </Typography.Text>
          ) : null}
        </Space>
      ),
      okText: '开始导入',
      onOk: async () => {
        setImporting(true);
        try {
          const result = await hq.tenant.importRegions({ items: rows, purgeManual });
          message.success(
            `导入完成：新增 ${result.inserted} 条 / 更新 ${result.updated} 条${
              purgeManual ? ` / 清理 ${result.purged} 条` : ''
            }`
          );
          await queryClient.invalidateQueries({ queryKey: ['regions'] });
          await queryClient.invalidateQueries({ queryKey: ['sys', 'region', 'options'] });
        } catch (err) {
          message.error(err instanceof ApiError ? err.friendlyMessage : String(err));
        } finally {
          setImporting(false);
        }
      }
    });
  };

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        行政区划
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        平台级区划字典（省 / 市 / 区县），数据源为随前端发布的全国行政区划（
        <Typography.Text code>public/china-regions.json</Typography.Text>
        ）。机构、分院、医共体、运营仓、仓库登记地址时统一在此级联选择；导入后编辑仅用于纠错与补充街道级。
      </Typography.Paragraph>

      {query.error ? (
        <Typography.Paragraph type="danger">
          {query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        </Typography.Paragraph>
      ) : null}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space size={32} wrap style={{ marginBottom: 12 }}>
          <Statistic title="本地数据（省/市/区）" value={sourceStats.total} suffix={`条`} loading={source === null && !sourceError} />
          <Statistic title="省" value={sourceStats.province} />
          <Statistic title="市" value={sourceStats.city} />
          <Statistic title="区县" value={sourceStats.district} />
          <Statistic title="库内区划" value={all.length} valueStyle={{ color: '#1677ff' }} />
          <Statistic title="库内（省/市/区）" value={`${dbStats.province}/${dbStats.city}/${dbStats.district}`} />
        </Space>

        <Space wrap size={[12, 12]}>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            loading={importing}
            disabled={!source?.length}
            onClick={() => (source?.length ? runImport(source) : void loadSource())}
          >
            导入全国行政区划
          </Button>
          <Tooltip title="勾选后，不在国标清单内且未被引用的手工区划会被逻辑删除">
            <Checkbox checked={purgeManual} onChange={(event) => setPurgeManual(event.target.checked)}>
              同时清理历史手工区划
            </Checkbox>
          </Tooltip>
          <Button icon={<PlusOutlined />} onClick={openCreate}>
            新增区划
          </Button>
          <Button onClick={() => void query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
        </Space>
      </Card>

      {sourceError ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="本地数据不可用" description={sourceError} />
      ) : null}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size={[12, 12]}>
          <Segmented options={LEVEL_FILTER} value={level} onChange={(value) => setLevel(Number(value))} />
          <Input.Search
            allowClear
            placeholder="搜索名称或编码"
            style={{ width: 220 }}
            onSearch={(value) => setKeyword(value)}
            onChange={(event) => {
              if (event.target.value === '') setKeyword('');
            }}
          />
          <Typography.Text type="secondary">
            共 {filtered.length} 条 / 全部 {all.length} 条
          </Typography.Text>
          {all.length === 0 ? (
            <Typography.Text type="warning">
              <EnvironmentOutlined /> 库内暂无区划，请先点「导入全国行政区划」
            </Typography.Text>
          ) : null}
        </Space>
      </Card>

      <Table<RegionNode>
        size="small"
        rowKey="regionCode"
        loading={query.isLoading}
        dataSource={treeData}
        pagination={false}
        expandable={{ defaultExpandAllRows: false }}
        columns={[
          { title: '区划名称', dataIndex: 'regionName', width: 260 },
          { title: '区划编码', dataIndex: 'regionCode', width: 150 },
          {
            title: '层级',
            dataIndex: 'regionLevel',
            width: 100,
            render: (value: number) => {
              const meta = LEVEL_META[value];
              return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>{value}</Tag>;
            }
          },
          {
            title: '上级编码',
            dataIndex: 'parentCode',
            width: 150,
            render: (value: string) => (value && value !== '0' ? value : '—')
          },
          {
            title: '中心点',
            key: 'coord',
            width: 200,
            render: (_, row) => (row.lng != null && row.lat != null ? `${row.lng}, ${row.lat}` : '—')
          },
          {
            title: '操作',
            key: 'action',
            width: 130,
            render: (_, row) => (
              <Space size={8}>
                <Typography.Link onClick={() => openEdit(row)}>编辑</Typography.Link>
                <Popconfirm
                  title={`删除区划「${row.regionName}」？`}
                  description="存在下级区划或已被租户引用时不允许删除"
                  onConfirm={() => void remove(row)}
                >
                  <Typography.Link type="danger">删除</Typography.Link>
                </Popconfirm>
              </Space>
            )
          }
        ]}
      />

      <Modal
        title={editing ? `编辑区划：${editing.regionName}` : '新增区划'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        okText="保存"
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="regionCode"
            label="区划编码"
            rules={[
              { required: true, message: '请输入区划编码' },
              // 与后端 ValidationRules.RegionCode 同口径：6–12 位。
              // 旧规则 2–12 会放过 "33" 这类值，提交后被后端拒，错误提示与前端规则还对不上。
              { pattern: /^\d{6,12}$/, message: '区划编码须为 6–12 位数字' }
            ]}
          >
            <Input placeholder="如 330111（富阳区）" maxLength={12} />
          </Form.Item>
          <Form.Item
            name="regionName"
            label="区划名称"
            rules={[
              { required: true, message: '请输入区划名称' },
              { max: 64, message: '不超过 64 字' }
            ]}
          >
            <Input placeholder="如 富阳区" maxLength={64} />
          </Form.Item>
          <Form.Item name="regionLevel" label="层级" rules={[{ required: true, message: '请选择层级' }]}>
            <Select
              options={LEVEL_OPTIONS}
              onChange={(value: number) => {
                // 顶级区划没有上级，避免残留上一级编码造成脏关联
                if (value === 1) form.setFieldValue('parentCode', undefined);
              }}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.regionLevel !== next.regionLevel}>
            {({ getFieldValue }) =>
              getFieldValue('regionLevel') === 1 ? null : (
                <Form.Item
                  name="parentCode"
                  label="上级区划（编码）"
                  rules={[{ required: true, message: '请选择上级区划' }]}
                >
                  <Select showSearch allowClear optionFilterProp="label" placeholder="按名称或编码搜索" options={parentOptions} />
                </Form.Item>
              )
            }
          </Form.Item>
          <Space size={16} style={{ display: 'flex' }}>
            {/* decimal(10,6)：范围与 6 位小数统一，避免与地址组件的 0.001 精度分裂 */}
            <Form.Item name="lng" label="中心点经度" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} min={-180} max={180} placeholder="如 119.960000" step={0.000001} precision={6} />
            </Form.Item>
            <Form.Item name="lat" label="中心点纬度" style={{ flex: 1 }}>
              <InputNumber style={{ width: '100%' }} min={-90} max={90} placeholder="如 30.049000" step={0.000001} precision={6} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
